import React, {
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect
} from 'react';
import { SelectProps, SelectHorizontalAlignmentEnum } from './types';
import { Popover } from '../Popover';
import {
  ArrowIcon,
  ModalContent,
  modalStyles,
  SelectContainer,
  selectHorizontalAlignment,
  SelectLabel,
  SelectText,
  TextsContainer
} from './styled';
import Icon from '../../Icon';
import ArrowRight from '../../../public/static/images/item/arrow-right.svg';
import useScreenSize from '../../../hooks/useScreenSize';
import { Modal } from '../Modal';
import { BackButtonIconEnum } from '../PageHeader/types';

export const Select = forwardRef(
  (
    {
      children,
      value,
      placeholder,
      cypress,
      width,
      stat,
      horizontalAlign = SelectHorizontalAlignmentEnum.Left,
      showIcon = true,
      styles,
      onClose,
      onOpenChange,
      modalTitle,
      isPlaceholderVisible = false,
      valueTag,
      showModalHeaderBorder = true,
      popoverStyles,
      stylesModal,
      closeIcon,
      closeButtonAlign,
      input,
      error,
      modalCypress,
      inputRef,
      ariaLabel,
      isWithHover = true
    }: SelectProps,
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const { isSmall } = useScreenSize();
    const prevIsOpenRef = useRef(isOpen);

    const handleFilterClick = (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
      e.preventDefault();
      if (e.target === inputRef?.current) {
        if (!isOpen) {
          setIsOpen(true);
        }
        return;
      }
      setIsOpen(!isOpen);
      if (isOpen) onClose?.();
    };

    const handleClose = () => {
      setIsOpen(false);
      onClose?.();
    };

    useEffect(() => {
      onOpenChange?.(isOpen);
      if (prevIsOpenRef.current && !isOpen) {
        onClose?.();
      }
      prevIsOpenRef.current = isOpen;
    }, [isOpen, onClose, onOpenChange]);

    useImperativeHandle(ref, () => ({
      closePopover: handleClose,
      openPopover: () => setIsOpen(true)
    }));

    useEffect(() => {
      if (isOpen && inputRef?.current && !isSmall) {
        inputRef?.current.focus();
      }
    }, [isSmall, isOpen, inputRef]);

    return (
      <SelectContainer
        ref={triggerRef}
        $isSelected={isOpen}
        type="button"
        onClick={handleFilterClick}
        data-cy={cypress}
        $hasValue={!!value}
        data-stat={stat}
        $styles={styles}
        $isError={!!error}
        aria-label={ariaLabel || 'Select'}
        $isWithHover={isWithHover}
      >
        <TextsContainer>
          {isPlaceholderVisible && ((isSmall && value) || !isSmall) && (
            <SelectLabel
              $isError={!!error}
              data-cy={`${cypress}-label`}
              tag={value ? 'body_2' : 'body_3'}
            >
              {placeholder}
            </SelectLabel>
          )}
          {(isOpen || value) && input}
          {!input && (
            <SelectText
              $isError={!!error}
              $hasValue={!!value}
              tag={valueTag || 'body_3'}
            >
              {value || placeholder}
            </SelectText>
          )}
        </TextsContainer>

        {showIcon && (
          <ArrowIcon $isSelected={isOpen}>
            <Icon
              indent={false}
              height={14}
              width={7}
              IconComponent={ArrowRight}
            />
          </ArrowIcon>
        )}
        {!isSmall ? (
          <Popover
            triggerRef={triggerRef}
            width={width}
            horizontalAlign={selectHorizontalAlignment(horizontalAlign)}
            isOpen={isOpen}
            closeHandler={handleClose}
            cypress={cypress}
            styles={popoverStyles}
          >
            {children}
          </Popover>
        ) : (
          <Modal
            isOpen={isOpen}
            onClose={handleClose}
            modalId={modalCypress}
            cypress={modalCypress}
            modalStyles={modalStyles(stylesModal)}
            title={modalTitle}
            closeButtonAlign={closeButtonAlign || 'right'}
            headerIcon={closeIcon || BackButtonIconEnum.Close}
            showHeaderBorder={showModalHeaderBorder}
            lockScroll={isSmall}
          >
            <ModalContent>{children}</ModalContent>
          </Modal>
        )}
      </SelectContainer>
    );
  }
);

Select.displayName = 'Select';
